/**
 * `[[slug]]` / `[[slug|표시 텍스트]]` 위키링크 토큰을 클릭 가능한 React 노드로 변환.
 *
 * - WikiMarkdown (위키 페이지 본문 렌더) 와 chat Markdown (LLM 답변 렌더) 양쪽에서 공통 사용.
 * - `onPeek` 가 있으면 chip 클릭 시 우측 미리보기 패널 트리거 (preventDefault 후 onPeek 호출).
 *   없으면 평소대로 `<Link to="/wikis/p/$slug">` 로 페이지 이동.
 * - `resolved` 가 주어지면 set 에 없는 슬러그를 빨갛게 line-through 표시 (끊긴 링크).
 */
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

export const WIKILINK_RE = /\[\[([^\]\n]+)\]\]/g;

/**
 * 끊긴 [[slug]] 가 포함된 문장 전체를 텍스트에서 제거.
 *
 * 문장 경계: `.` `!` `?` `。` 또는 문자열 시작/끝. 한국어는 마침표가 안 붙는 경우도 많아
 * 줄바꿈 / 리스트 bullet 단위로도 끊는다 (이미 ReactMarkdown 이 paragraph·li 단위 children
 * 으로 쪼개주므로 child 안에선 줄바꿈이 거의 없음). 충돌 범위는 merge.
 */
function stripBrokenSentences(text: string, resolved: Set<string>): string {
  const ranges: Array<[number, number]> = [];
  for (const m of text.matchAll(WIKILINK_RE)) {
    const inner = m[1] ?? '';
    const slug = (inner.split('|')[0] ?? '').trim();
    if (!slug || resolved.has(slug)) continue;
    const start = m.index ?? 0;
    const end = start + m[0].length;
    let sStart = start;
    while (sStart > 0 && !/[.!?。\n]/.test(text[sStart - 1] ?? '')) sStart--;
    let sEnd = end;
    while (sEnd < text.length && !/[.!?。\n]/.test(text[sEnd] ?? '')) sEnd++;
    if (sEnd < text.length) sEnd++; // include the terminator
    // 좌측 공백·중점·구두점은 다음 문장으로 흡수되게 한 칸씩 줄여둠
    while (sStart < sEnd && /[\s·,]/.test(text[sStart] ?? '')) sStart++;
    ranges.push([sStart, sEnd]);
  }
  if (ranges.length === 0) return text;
  ranges.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const r of ranges) {
    const last = merged[merged.length - 1];
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]);
    else merged.push([r[0], r[1]]);
  }
  let out = '';
  let pos = 0;
  for (const [s, e] of merged) {
    out += text.slice(pos, s);
    pos = e;
  }
  out += text.slice(pos);
  return out;
}

export interface WikilinkOpts {
  /** 페이지 존재가 확인된 슬러그 set. 없으면 모두 정상 링크로 처리. */
  resolved?: Set<string>;
  /** 정의되어 있으면 chip 클릭 시 페이지 이동 대신 이 콜백 호출. */
  onPeek?: (slug: string) => void;
  /**
   * `[[slug]]` 의 chip 표시 텍스트를 슬러그 대신 페이지 제목으로 보여주기 위한 lookup.
   * - 사용자가 `[[slug|표시텍스트]]` 형태로 명시했으면 그 텍스트가 우선.
   * - 그 외엔 `slugToTitle.get(slug) ?? slug` 로 결정.
   */
  slugToTitle?: Map<string, string>;
  /**
   * 페이지 이동 Link 에 추가할 query string. 챗 컨텍스트(?from=chat) 등 전파용.
   * onPeek 가 우선이라 onPeek 있으면 무시. resolved 가 broken 인 경우도 무시.
   */
  linkSearchParam?: string;
}

export function transformWikilinks(children: ReactNode, opts: WikilinkOpts = {}): ReactNode {
  const arr = Array.isArray(children) ? children : [children];
  const out: ReactNode[] = [];
  let key = 0;
  for (const rawChild of arr) {
    if (typeof rawChild !== 'string') {
      out.push(rawChild);
      continue;
    }
    // 끊긴/권한없는 [[slug]] 가 포함된 문장은 먼저 통째로 제거 (정보 누출 방지).
    const child = opts.resolved ? stripBrokenSentences(rawChild, opts.resolved) : rawChild;
    if (!child) continue;
    let lastIndex = 0;
    for (const m of child.matchAll(WIKILINK_RE)) {
      const start = m.index ?? 0;
      const end = start + m[0].length;
      if (start > lastIndex) {
        out.push(child.slice(lastIndex, start));
      }
      const inner = m[1] ?? '';
      const [slugRaw, displayRaw] = inner.split('|');
      const slug = (slugRaw ?? '').trim();
      // 우선순위: 사용자 명시 표시 텍스트 > slugToTitle 매핑 > slug 그대로.
      const display =
        displayRaw !== undefined ? displayRaw.trim() : (opts.slugToTitle?.get(slug) ?? slug);
      const isBroken = opts.resolved !== undefined && !opts.resolved.has(slug);
      if (!slug) {
        out.push(m[0]);
      } else if (isBroken) {
        // 안전망 — stripBrokenSentences 가 사전 제거하므로 여기 도달 거의 없음.
        // 도달하면 슬러그 자체도 출력 안 함.
      } else if (opts.onPeek) {
        const handler = opts.onPeek;
        out.push(
          <button
            key={`wl-${key++}`}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handler(slug);
            }}
            title={`위키 미리보기: ${slug}`}
            className="inline rounded-sm bg-accent/10 px-1 text-accent underline-offset-2 hover:bg-accent/20 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
          >
            {display}
          </button>,
        );
      } else {
        const href = `/wikis/p/${encodeURIComponent(slug)}${opts.linkSearchParam ? `?${opts.linkSearchParam}` : ''}`;
        out.push(
          <Link
            key={`wl-${key++}`}
            to={href}
            className="text-accent underline underline-offset-2 hover:opacity-80"
          >
            {display}
          </Link>,
        );
      }
      lastIndex = end;
    }
    if (lastIndex < child.length) {
      out.push(child.slice(lastIndex));
    }
  }
  return out;
}
